import { z, zodResolver } from "@pos/shared";
import {
  Button,
  Input,
  Field,
  FieldLabel,
  FieldError,
  FieldGroup,
  useForm,
  Controller,
} from "@pos/ui";

interface StoreConfigFormProps {
  onSubmit: (shopId: string) => void;
  isLoading: boolean;
  error: string | null;
}

const storeConfigSchema = z.object({
  shopId: z.string().min(1, "Store ID is required"),
});

type StoreConfigFormValues = z.infer<typeof storeConfigSchema>;

export function StoreConfigForm({
  onSubmit,
  isLoading,
  error,
}: StoreConfigFormProps) {
  const form = useForm<StoreConfigFormValues>({
    resolver: zodResolver(storeConfigSchema),
    defaultValues: {
      shopId: "",
    },
  });

  function handleSubmit(values: StoreConfigFormValues) {
    onSubmit(values.shopId);
  }

  return (
    <div className="w-full">
      <div className="mb-6 space-y-1">
        <h2 className="text-3xl font-bold tracking-tight">
          Store Configuration
        </h2>
        <p className="text-base text-muted-foreground">
          Enter your store ID to connect this device
        </p>
      </div>

      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <FieldGroup>
          {error && (
            <div className="rounded-md bg-destructive/10 px-3 py-2 text-base text-destructive">
              {error}
            </div>
          )}

          <Controller
            name="shopId"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel>Store ID</FieldLabel>
                <Input
                  {...field}
                  placeholder="Enter your store ID"
                  aria-invalid={fieldState.invalid}
                  disabled={isLoading}
                />
                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            )}
          />

          <Button
            type="submit"
            size="lg"
            className="w-full"
            disabled={isLoading}
          >
            {isLoading ? "Connecting..." : "Connect Store →"}
          </Button>

          <p className="text-center text-sm text-muted-foreground">
            Contact your administrator if you don't have a store ID.
          </p>
        </FieldGroup>
      </form>
    </div>
  );
}
